import React from 'react';
import { SeriesZonesOptionsObject } from 'highcharts';
import i18next from 'i18next';
import ReactDOMServer from 'react-dom/server';
import { Colours } from '@components-clean/central-monitoring/panels/panel-constants';
import { getScoreColor } from '@components-clean/core/charts/scatter-plot/utils';
import { FormatNumberForChart } from '@components-clean/core/charts/shared/number-utils';
import { parseDateField } from '@components-clean/core/grid-fe/utils';
import {
    ChartHorizontalLine,
    LineResult,
    LineSeriesData,
    SeriesConfig,
    SparkLineSeriesData,
} from '@interfaces-clean/panels.interface';
import { PanelAverageLineThresholdType, PanelSeriesType } from 'types/graphql-global-types';

export const siteColours = [Colours.PURPLE, Colours.GOLD, Colours.LIGHT_BLUE, Colours.DARK_BLUE];

export const getSubtitle = (
    seriesConfig: SeriesConfig,
    threshold1?: number,
    threshold2?: number,
    thresholdType?: PanelAverageLineThresholdType | null,
) => {
    switch (seriesConfig.type) {
        case PanelSeriesType.SUBJECT_BY_VISIT:
            return ReactDOMServer.renderToStaticMarkup(
                <span>
                    {i18next.t('admin:lowerThresholdValue')}: {FormatNumberForChart(threshold1)}{' '}
                    {i18next.t('admin:upperThresholdValue')}: {FormatNumberForChart(threshold2)}
                </span>,
            );
        case PanelSeriesType.SITE_AVERAGE:
            const markupAverage = (
                <>
                    {threshold1 != null && (
                        <div key="threshold1">
                            <span
                                style={{
                                    color: Colours.AMBER,
                                    fontSize: 'large',
                                    marginRight: '5px',
                                }}
                            >
                                - - -
                            </span>
                            {thresholdType === PanelAverageLineThresholdType.STUDY_MEDIAN_MEAN
                                ? i18next.t('admin:STUDY_MEDIAN')
                                : i18next.t('admin:STUDY_SPECIFIC')}
                            {`: ${FormatNumberForChart(threshold1)}`}
                        </div>
                    )}
                    {threshold2 != null && (
                        <div key="threshold2">
                            <span
                                style={{
                                    color: Colours.RED,
                                    fontSize: 'large',
                                    marginRight: '5px',
                                }}
                            >
                                - - -
                            </span>
                            {thresholdType === PanelAverageLineThresholdType.STUDY_MEDIAN_MEAN
                                ? i18next.t('admin:STUDY_MEAN')
                                : i18next.t('admin:STUDY_SPECIFIC')}
                            {`: ${FormatNumberForChart(threshold2)}`}
                        </div>
                    )}
                </>
            );
            return ReactDOMServer.renderToStaticMarkup(markupAverage);
        case PanelSeriesType.SITE_CUMULATIVE:
            const markupAccumulative = (
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                    {seriesConfig.graphSeries?.map((series, index) => {
                        if (series == null || series.threshold == null) return;

                        return (
                            <div
                                style={{ display: 'flex', alignItems: 'center' }}
                                key={`${series.label}-${series.threshold}`}
                            >
                                <span
                                    style={{
                                        color: siteColours[index % siteColours.length],
                                        fontSize: 'large',
                                        marginRight: '5px',
                                    }}
                                >
                                    - - -
                                </span>
                                {FormatNumberForChart(series.threshold)}
                            </div>
                        );
                    })}
                </div>
            );
            return ReactDOMServer.renderToStaticMarkup(markupAccumulative);
        default:
            break;
    }
};

export const getLineSubtitleComponents = (
    seriesConfig: SeriesConfig,
    threshold1?: number,
    threshold2?: number,
    thresholdType?: PanelAverageLineThresholdType | null,
) => {
    switch (seriesConfig.type) {
        case PanelSeriesType.SUBJECT_BY_VISIT:
            if (!threshold1 && !threshold2) return undefined;
            // For some bizzaro reason, we decide to save threshold1 and 2 as -1 instead of null
            if (threshold1 && threshold1 < 0 && threshold2 && threshold2 < 0) return undefined;
            return (
                <div className="k-flex-column">
                    <div>
                        <span
                            style={{
                                marginRight: '8px',
                            }}
                        >
                            {i18next.t('admin:lowerThresholdValue')}:
                        </span>
                        {FormatNumberForChart(threshold1)}
                    </div>
                    <div>
                        <span
                            style={{
                                marginRight: '8px',
                            }}
                        >
                            {i18next.t('admin:upperThresholdValue')}:
                        </span>
                        {FormatNumberForChart(threshold2)}
                    </div>
                </div>
            );

        case PanelSeriesType.SITE_AVERAGE:
            return (
                <>
                    {threshold1 != null && (
                        <div className="k-justify-items-left">
                            <span
                                style={{
                                    fontWeight: '500',
                                }}
                            >
                                {thresholdType === PanelAverageLineThresholdType.STUDY_MEDIAN_MEAN
                                    ? i18next.t('admin:STUDY_MEDIAN')
                                    : i18next.t('admin:STUDY_SPECIFIC')}
                            </span>
                            <div>
                                <span
                                    style={{
                                        color: Colours.AMBER,
                                        fontSize: 'large',
                                        marginRight: '12px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    - - -
                                </span>
                                {FormatNumberForChart(threshold1)}
                            </div>
                        </div>
                    )}
                    {threshold2 != null && (
                        <div className="k-justify-items-left">
                            <span
                                style={{
                                    fontWeight: '500',
                                }}
                            >
                                {thresholdType === PanelAverageLineThresholdType.STUDY_MEDIAN_MEAN
                                    ? i18next.t('admin:STUDY_MEAN')
                                    : i18next.t('admin:STUDY_SPECIFIC')}
                            </span>
                            <div>
                                <span
                                    style={{
                                        color: Colours.RED,
                                        fontSize: 'large',
                                        marginRight: '12px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    - - -
                                </span>
                                {FormatNumberForChart(threshold2)}
                            </div>
                        </div>
                    )}
                </>
            );

        case PanelSeriesType.SITE_CUMULATIVE:
            if (!seriesConfig?.graphSeries?.find((s) => s?.threshold != null)) return undefined;
            return (
                <div className="k-flex-column k-gap-1">
                    {seriesConfig.graphSeries?.map((series, index) => {
                        if (series == null || series.threshold == null) return;

                        return (
                            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
                            // eslint-disable-next-line @eslint-react/no-missing-key
                            <div>
                                <span
                                    style={{
                                        color: siteColours[index % siteColours.length],
                                        fontSize: 'large',
                                        marginRight: '12px',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    - - -
                                </span>
                                {FormatNumberForChart(series.threshold)}
                            </div>
                        );
                    })}
                </div>
            );
        default:
            break;
    }
};

export const getHorizontalLines = (
    seriesConfig: SeriesConfig,
    threshold1?: number,
    threshold2?: number,
): ChartHorizontalLine[] => {
    const { type } = seriesConfig;

    const horizontalLines: ChartHorizontalLine[] = [];

    switch (type) {
        case PanelSeriesType.SITE_AVERAGE:
            if (threshold1 != null)
                horizontalLines.push({
                    color: Colours.AMBER,
                    style: 'Dash',
                    y: threshold1,
                });

            if (threshold2 != null)
                horizontalLines.push({
                    color: Colours.RED,
                    style: 'Dash',
                    y: threshold2,
                });
            break;
        case PanelSeriesType.SITE_CUMULATIVE:
            seriesConfig.graphSeries?.forEach((value, index) => {
                if (value.threshold != null)
                    horizontalLines.push({
                        color: siteColours[index % siteColours.length],
                        style: 'Dash',
                        y: value.threshold,
                    });
            });
            break;
        case PanelSeriesType.SUBJECT_BY_VISIT:
            if (threshold1 != null)
                horizontalLines.push({
                    color: Colours.AMBER,
                    style: 'Dash',
                    y: threshold1,
                });

            if (threshold2 != null)
                horizontalLines.push({
                    color: Colours.RED,
                    style: 'Dash',
                    y: threshold2,
                });
            break;
        default:
            break;
    }

    return horizontalLines;
};

const siteMarkers = ['circle', 'square', 'diamond', 'triangle'];

export const getYValueBounds = (
    data: LineSeriesData[],
    threshold1?: number,
    threshold2?: number,
): [number?, number?] => {
    // No need to workout bounds if there are no thresholds
    if (threshold1 == null && threshold2 == null) return [undefined, undefined];

    let minY: number | undefined;
    let maxY: number | undefined;

    data.forEach((series) => {
        series.data.forEach((point) => {
            if (point.y === undefined) return;

            if (minY === undefined || point.y < minY) minY = point.y;
            if (maxY === undefined || point.y > maxY) maxY = point.y;
        });
    });

    let lowerBounds: number | undefined;
    let upperBounds: number | undefined;
    if (threshold1 != null && minY != null) lowerBounds = Math.min(threshold1, minY);
    if (threshold2 != null && maxY != null) upperBounds = Math.max(threshold2, maxY);

    return [lowerBounds, upperBounds];
};

export const getSeriesData = (data: LineResult, seriesConfig?: SeriesConfig): LineSeriesData[] => {
    if (!seriesConfig) return [];

    const { type } = seriesConfig;

    switch (type) {
        case PanelSeriesType.SITE:
        case PanelSeriesType.SITE_CUMULATIVE:
            if (!data.lineSiteForStudy) return [];
            return data.lineSiteForStudy.map<LineSeriesData>((series, index) => {
                const name =
                    seriesConfig.graphSeries && index < seriesConfig.graphSeries?.length
                        ? seriesConfig.graphSeries[index].label
                        : '';

                return {
                    name: name ?? '',
                    marker: siteMarkers[index % siteMarkers.length],
                    data: series.map((d) => ({
                        x: parseDateField(d.x)?.getTime(),
                        y: typeof d.y === 'number' ? d.y : undefined,
                    })),
                };
            });
        case PanelSeriesType.SITE_AVERAGE:
            if (!data.lineAverageForStudy) return [];
            return [
                {
                    name: i18next.t('admin:Site'),
                    data: data.lineAverageForStudy.map((d) => ({
                        x: parseDateField(d.x)?.getTime(),
                        y: typeof d.y === 'number' ? d.y : undefined,
                    })),
                },
            ];

        case PanelSeriesType.SUBJECT:
            if (!data.lineSubjectForSubjects) return [];
            return Object.keys(data.lineSubjectForSubjects).map<LineSeriesData>((key) => {
                // @ts-ignore
                const item = data.lineSubjectForSubjects[key];

                return {
                    name: item?.subjectNumber ?? '',
                    data:
                        item?.data?.map((v) => ({
                            x: parseDateField(v.x)?.getTime(),
                            y: typeof v.y === 'number' ? v.y : undefined,
                        })) ?? [],
                };
            });
        case PanelSeriesType.SUBJECT_BY_VISIT:
            if (!data.lineSubjectByVisitForStudy) return [];
            return Object.keys(data.lineSubjectByVisitForStudy).map<LineSeriesData>((key) => {
                // @ts-ignore
                const item = data.lineSubjectByVisitForStudy[key];
                const zones: SeriesZonesOptionsObject[] = [];
                const points: { x?: number; y?: number }[] = [];

                // We want to connect nulls with dotted lines. We can achieve this via zones
                // Setup zones along the x-axis that will style the line with dashes when the y value is null
                // and keep it solid when there is a value
                if (item.visits != null) {
                    for (let i = 1; i < item.visits.length; i++) {
                        const prev = item.visits[i - 1];
                        const curr = item.visits[i];

                        if (i === 1) points.push({ x: prev.index, y: prev.value ?? undefined });
                        points.push({ x: curr.index, y: curr.value ?? undefined });

                        if (prev.value !== null && curr.value === null) {
                            zones.push({
                                value: i - 1,
                                dashStyle: 'Solid',
                            });
                        } else if (prev.value === null && curr.value !== null) {
                            zones.push({
                                value: i,
                                dashStyle: 'Dash',
                            });
                        }
                    }
                }

                return {
                    name: item.subjectNumber ?? '',
                    data: points,
                    zoneAxis: 'x',
                    zones,
                };
            });
        case PanelSeriesType.EVENT:
            if (data.lineEventForStudy == null) return [];
            return Object.keys(data.lineEventForStudy).map((key) => ({
                name: key,
                // @ts-ignore
                data: data.lineEventForStudy[key].map((d) => ({
                    x: parseDateField(d.x)?.getTime(),
                    y: typeof d.y === 'number' ? d.y : undefined,
                })),
            }));
        default:
            return [];
    }
};

export const getSparkSeriesData = (data: SparkLineSeriesData): LineSeriesData[] => {
    if (!data.sparkLineData) return [];
    return data.sparkLineData.map<LineSeriesData>((series, index) => {
        return {
            name: '',
            marker: siteMarkers[index % siteMarkers.length],
            data: series.map((d, idx) => ({
                x: idx,
                y: d.y,
                color: d.color ? getScoreColor(d.color) : undefined,
                colorIndex: d.color ? d.color : undefined,
                score: d.score ? d.score : undefined,
                evaluationModel: 'evaluationModel' in d ? d.evaluationModel : undefined,
            })),
        };
    });
};
